/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useMemo } from 'react'
import { WarningOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import BaseModal from '../../ts-components/BaseModal'
import Button from '../../ts-components/Button'
import Text from '../../ts-components/Text'
import type { AudienceBreakdownData } from './AudienceBreakdownSection'
import {
  calculateAudienceReach,
  inAppFootnote,
  type SelectedChannels,
} from '../utils/audienceReach'
import { audienceLegendRows } from '../utils/audienceGroups'

interface NotificationWarningModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
  audienceData: AudienceBreakdownData | null
  isLoadingAudience: boolean
  /** Ticked channels — the same input the panel's donut derives from. */
  channels: SelectedChannels
  includeUnjoined: boolean
  onViewRecipients?: () => void
}

/**
 * The last screen before an irreversible send.
 *
 * Its numbers are the SAME derivation the audience panel behind it uses
 * (utils/audienceReach), not a second hand-rolled tally: if the two disagree, the
 * feature has failed its main requirement.
 */
export const NotificationWarningModal: React.FC<
  NotificationWarningModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  audienceData,
  isLoadingAudience,
  channels,
  includeUnjoined,
  onViewRecipients,
}) => {
  const reach = useMemo(
    () => calculateAudienceReach(audienceData, channels, includeUnjoined),
    [audienceData, channels, includeUnjoined]
  )

  const notificationTypes = useMemo(() => {
    const types: string[] = []
    if (channels.sendViaEmail) {
      types.push('email')
    }
    if (channels.sendViaPush) {
      types.push('push notification')
    }

    return types.join(' and ')
  }, [channels.sendViaEmail, channels.sendViaPush])

  const footnote = inAppFootnote(reach, channels)

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title='Share This Update With The Selected Audience?'
      desktopWidth='480px'
      actions={
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            width: 100%;
          `}
        >
          <Button
            text={isLoading ? 'POSTING...' : 'SEND NOTIFICATIONS'}
            onClick={onConfirm}
            disabled={isLoading}
            isLoading={isLoading}
            variant='primary'
            style={css`
              width: 100%;
            `}
          />
          <Button
            text='CANCEL'
            onClick={onClose}
            disabled={isLoading}
            variant='secondary'
            style={css`
              width: 100%;
            `}
          />
        </div>
      }
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        `}
      >
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.WARNING_200};
          `}
        />

        <div
          css={css`
            align-self: stretch;
            text-align: center;
          `}
        >
          <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
            You are about to send{' '}
            <span
              css={css`
                font-weight: 700;
              `}
            >
              {notificationTypes}
            </span>{' '}
            notifications to all users in your selected audience. This action
            cannot be undone.
          </Text>
        </div>

        <div
          css={css`
            align-self: stretch;
            background: ${colors.SECONDARY_200};
            border-radius: 8px;
            padding: 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <Text variant='paragraphSmall' weight='semiBold' color={colors.WHITE}>
            Target Audience Breakdown
          </Text>

          {isLoadingAudience ? (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_100}
            >
              Loading audience data...
            </Text>
          ) : audienceData ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
              `}
            >
              {/*
                The SAME rows, from the SAME selector, as the donut legend on the panel
                behind this dialog. They used to be two hand-kept lists reading two
                different sources — this one the legacy `*_users` counts, the panel the
                reach — so the two screens printed different numbers under identical
                labels, on top of dropping every group whose members had all not joined.

                "User" is gone with them: most of this ticket's point is the people here
                who do not have an account.
              */}
              {audienceLegendRows(reach).map(({ key, label, value }) => (
                <Text
                  key={key}
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  {label}: {value.toLocaleString()}
                </Text>
              ))}

              <div
                css={css`
                  margin-top: 8px;
                  padding-top: 8px;
                  border-top: 1px solid ${colors.SECONDARY_100};
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='semiBold'
                  color={colors.WHITE}
                >
                  Recipients: {reach.total.toLocaleString()}
                </Text>

                {reach.unjoined > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                  >
                    Includes {reach.unjoined.toLocaleString()} who have not
                    joined
                  </Text>
                )}

                {footnote && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_100}
                  >
                    {footnote}
                  </Text>
                )}

                {reach.noEmail > 0 && (
                  <Text
                    variant='paragraphXSmall'
                    weight='regular'
                    color={colors.NEUTRAL_100}
                  >
                    {reach.noEmail.toLocaleString()} cannot be reached by email
                  </Text>
                )}

                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_100}
                >
                  of {reach.inAudience.toLocaleString()} in audience
                </Text>
              </div>

              {onViewRecipients && (
                <button
                  type='button'
                  onClick={onViewRecipients}
                  css={css`
                    align-self: flex-start;
                    background: none;
                    border: none;
                    padding: 4px 0 0;
                    cursor: pointer;
                    &:hover p {
                      text-decoration: underline;
                    }
                  `}
                >
                  <Text
                    variant='paragraphXSmall'
                    weight='medium'
                    color={colors.PRIMARY}
                  >
                    View Audience
                  </Text>
                </button>
              )}
            </div>
          ) : (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_100}
            >
              Unable to load audience data
            </Text>
          )}
        </div>

        <div
          css={css`
            align-self: stretch;
            text-align: center;
          `}
        >
          <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
            Are you sure you want to continue?
          </Text>
        </div>
      </div>
    </BaseModal>
  )
}
